import { JumpsGame, JumpColor } from './JumpsGame.js';
import { Grid } from '../../core/Grid.js';
import { Position, Direction, positionKey, parsePositionKey } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import {
    TweenScheduler, MOTION, turnDelta, PieceSet, Ranked, pruneMemo
} from '../../rendering/juice.js';

export interface JumpsRenderConfig extends Partial<RenderConfig> {
    rampWidth?: number;
    arcWidth?: number;
    buildingColor?: string;
    landingRadius?: number;
    boundaryColor?: string;
    boundaryWidth?: number;
}

const JUMPS_DEFAULTS = {
    rampWidth: 6,
    arcWidth: 2,
    buildingColor: COLORS.blue,
    landingRadius: 16,
    launcherSize: 13,
    boundaryColor: COLORS.gray,
    boundaryWidth: 6
};

// ── Ramp animation (docs/juice.md) ────────────────────────────────────
//
// A ramp is built landing → launcher, one cell at a time, exactly as an antenna
// is, and it is drawn the same way: the body is a run of links, each stroked on
// from its landing-side end. Same reason, too — the ramp knows its own direction,
// so no gesture is read.
//
// Everything is keyed by *where it sits*, so a build becoming a finished ramp
// changes colour and gains a launcher without any of it being redrawn.
//
// The jump arcs are simulation and stay put: the hop is what the board works out
// from the ramp, not something the player placed.

interface LinkInfo extends Ranked {
    /** The landing-side end — the end a link is drawn on from. */
    from: Position;
    to: Position;
    color: string;
}

interface LandingInfo extends Ranked {
    color: string;
}

interface LauncherInfo extends Ranked {
    dir: Direction;
    color: string;
}

/** A turn of a launcher, from wherever it was when the player cycled it. */
interface LauncherSpin { from: number; to: number; start: number; }

export class JumpsRenderer extends GridRenderer {
    private jumpsConfig: typeof JUMPS_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private links = new PieceSet(this.tweens);
    private landings = new PieceSet(this.tweens);
    private launchers = new PieceSet(this.tweens);
    /** What each piece looked like on its last real frame — read for the ones that
     *  are on their way out, since those are already gone from game state. */
    private linkMemo = new Map<string, LinkInfo>();
    private landingMemo = new Map<string, LandingInfo>();
    private launcherMemo = new Map<string, LauncherInfo>();
    private linkFrame = new Map<string, number>();
    private landingFrame = new Map<string, number>();
    private launcherFrame = new Map<string, number>();
    /** Launcher headings, accumulated rather than renormalised. */
    private launcherAngle = new Map<string, number>();
    private launcherSpin = new Map<string, LauncherSpin>();
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: JumpsGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: JumpsRenderConfig = {}) {
        super(canvas, grid, config);
        this.jumpsConfig = { ...JUMPS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every ramp at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.links.clear();
        this.landings.clear();
        this.launchers.clear();
        this.launcherSpin.clear();
    }

    private color(cat: JumpColor): string {
        return cat === 'teal' ? COLORS.teal : cat === 'red' ? COLORS.red : COLORS.amber;
    }

    render(game: JumpsGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        // One pass over the chain: the ramp colours drive what is drawn *and* how the
        // fan-out of a bulk change is ordered, and working them out isn't free.
        const colors = game.computeRampColors();
        this.syncPieces(game, colors);

        this.clear();
        this.drawGrid();
        this.drawQuarterBoundaries(game);

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawDigitGhosts(game);
        this.drawJumpArcs(game, colors);
        this.drawRamps(game, colors);
        this.drawDepartingPieces();
        // The building ramp's body goes here, *under* the gates, so its round line-caps at a
        // gate cell are covered by the gate square — exactly like a finished ramp's body.
        this.drawCurrentPathBody(game);
        this.drawWarnings(game);

        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
            this.drawVortexGateDashedLines(game);
        }

        // The building markers go *over* the gates: a ramp built through one ends on the
        // paired cell, so its circle has to sit centered on top of that square to show where
        // the next click carries on from.
        this.drawCurrentPathMarkers(game);

        this.drawDigits(grid);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    /** Scoring overlay: the primary metric counts launchers, so redraw those in amber. */
    protected drawScoringPrimary(game: JumpsGame): void {
        for (const [, ramp] of game.getRamps()) {
            this.drawLauncher(
                game.getLauncher(ramp), COLORS.amber, this.dirAngle(ramp.jumpDirection), 1
            );
        }
    }

    // ── Ramp animation (docs/juice.md) ────────────────────────────────

    /** Diff all three families against the last frame, then work out where every
     *  moving piece sits this one. */
    private syncPieces(game: JumpsGame, colors: ReturnType<JumpsGame['computeRampColors']>): void {
        const chain = this.chainOrder(game);
        const links = this.collectLinks(game, colors, chain);
        const landings = this.collectLandings(game, colors, chain);
        const launchers = this.collectLaunchers(game, colors, chain);

        const ranked = <T extends Ranked>(next: Map<string, T>, memo: Map<string, T>) =>
            (key: string, entering: boolean) => entering
                ? next.get(key)?.enterRank ?? 0
                : memo.get(key)?.exitRank ?? 0;

        // The memos still hold the last real frame here, which is what the pieces on
        // their way out are ranked and drawn by; they are refreshed below.
        this.links.sync(new Set(links.keys()), ranked(links, this.linkMemo));
        this.landings.sync(new Set(landings.keys()), ranked(landings, this.landingMemo));
        const arrived = this.launchers.sync(new Set(launchers.keys()), ranked(launchers, this.launcherMemo));

        for (const [key, info] of links) this.linkMemo.set(key, info);
        for (const [key, info] of landings) this.landingMemo.set(key, info);
        for (const [key, info] of launchers) this.launcherMemo.set(key, info);

        this.linkFrame = this.links.frames();
        this.landingFrame = this.landings.frames();
        this.launcherFrame = this.launchers.frames();
        this.animating = this.linkFrame.size > 0 || this.landingFrame.size > 0
            || this.launcherFrame.size > 0;

        this.syncLauncherSpins(launchers, arrived);

        pruneMemo(this.linkMemo, this.links.state, this.linkFrame);
        pruneMemo(this.landingMemo, this.landings.state, this.landingFrame);
        pruneMemo(this.launcherMemo, this.launchers.state, this.launcherFrame);
        pruneMemo(this.launcherAngle, this.launchers.state, this.launcherFrame);
        pruneMemo(this.launcherSpin, this.launchers.state, this.launcherFrame);
    }

    /** Order the ramps the way the cycle walks: by the digit each one lands on,
     *  with the routing ramps — which land on no digit — after them in reading order. */
    private chainOrder(game: JumpsGame): Map<number, number> {
        const ranked = [...game.getRamps()].map(([id, ramp]) => {
            const landing = game.getLanding(ramp);
            const digit = game.getGrid().getDigitAt(landing);
            return {
                id,
                key: digit ?? 100 + landing.row * this.grid.size + landing.col
            };
        });
        ranked.sort((x, y) => x.key - y.key);
        return new Map(ranked.map((entry, i) => [entry.id, i]));
    }

    /** A body link, keyed by the pair of cells it joins whichever way round. */
    private linkKey(a: Position, b: Position): string {
        return (a.row < b.row || (a.row === b.row && a.col < b.col))
            ? `${a.row},${a.col},${b.row},${b.col}`
            : `${b.row},${b.col},${a.row},${a.col}`;
    }

    private collectLinks(
        game: JumpsGame,
        colors: ReturnType<JumpsGame['computeRampColors']>,
        chain: Map<number, number>
    ): Map<string, LinkInfo> {
        const links = new Map<string, LinkInfo>();

        // Runs, not the raw path: a ramp through a gate has no line between the gate
        // and its pair, so there is no link there either.
        const addRuns = (runs: Position[][], length: number, color: string, seat: number) => {
            let step = 0;
            for (const run of runs) {
                for (let i = 1; i < run.length; i++) {
                    step++;
                    links.set(this.linkKey(run[i - 1], run[i]), {
                        from: run[i - 1], to: run[i], color,
                        enterRank: seat * 100 + step,
                        exitRank: seat * 100 + (length - step)
                    });
                }
                step++;   // the teleport into the next run: a step with no line to draw
            }
        };

        for (const [id, ramp] of game.getRamps()) {
            addRuns(game.getBodyRuns(ramp), ramp.path.length,
                this.color(game.getRampColor(ramp, colors)), chain.get(id) ?? 0);
        }
        // The build in progress sits ahead of the finished ramps: it is the newest
        // thing on the board and the only one the player is holding.
        addRuns(game.getBuildingRuns(), game.getCurrentPath().length,
            this.jumpsConfig.buildingColor, -1);
        return links;
    }

    private collectLandings(
        game: JumpsGame,
        colors: ReturnType<JumpsGame['computeRampColors']>,
        chain: Map<number, number>
    ): Map<string, LandingInfo> {
        const landings = new Map<string, LandingInfo>();

        for (const [id, ramp] of game.getRamps()) {
            const seat = (chain.get(id) ?? 0) * 100;
            landings.set(positionKey(game.getLanding(ramp)), {
                color: this.color(game.getLandingColor(ramp, colors)),
                enterRank: seat, exitRank: seat + ramp.path.length
            });
        }

        const path = game.getCurrentPath();
        if (path.length > 0) {
            landings.set(positionKey(path[0]), {
                color: this.jumpsConfig.buildingColor,
                enterRank: -100, exitRank: -100 + path.length
            });
        }
        return landings;
    }

    private collectLaunchers(
        game: JumpsGame,
        colors: ReturnType<JumpsGame['computeRampColors']>,
        chain: Map<number, number>
    ): Map<string, LauncherInfo> {
        const launchers = new Map<string, LauncherInfo>();
        for (const [id, ramp] of game.getRamps()) {
            const seat = (chain.get(id) ?? 0) * 100;
            launchers.set(positionKey(game.getLauncher(ramp)), {
                dir: ramp.jumpDirection,
                color: this.color(game.getLauncherColor(ramp, colors)),
                enterRank: seat + ramp.path.length, exitRank: seat
            });
        }
        return launchers;
    }

    /**
     * Cycling a launcher turns it to its other legal jump (`change`). A quarter only
     * ever offers one horizontal and one vertical direction, so every turn is a
     * quarter-circle and there is never a half-turn to break the tie on. A newly
     * placed launcher opens on its heading rather than turning to it.
     */
    private syncLauncherSpins(launchers: Map<string, LauncherInfo>, arrived: Set<string>): void {
        const now = this.tweens.now();
        for (const [key, info] of launchers) {
            const target = this.dirAngle(info.dir);
            const rest = this.launcherAngle.get(key);
            if (rest === undefined || arrived.has(key) || !this.tweens.isEnabled()) {
                this.launcherAngle.set(key, target);
                this.launcherSpin.delete(key);
                continue;
            }
            const delta = turnDelta(rest, target, 1);
            if (Math.abs(delta) < 1e-6) continue;
            // Retarget from where the triangle actually is, so a click landing mid-turn
            // bends the motion instead of restarting it.
            const from = this.launcherAngleAt(key, now);
            this.launcherAngle.set(key, rest + delta);
            this.launcherSpin.set(key, { from, to: rest + delta, start: now });
        }
    }

    /** A launcher's heading this frame, mid-turn or at rest. */
    private launcherAngleAt(key: string, now: number = this.tweens.now()): number {
        const rest = this.launcherAngle.get(key) ?? 0;
        const spin = this.launcherSpin.get(key);
        if (!spin) return rest;

        const { value, done } = this.tweens.tween(spin.start, MOTION.change, now);
        if (done) {
            this.launcherSpin.delete(key);
            return rest;
        }
        this.animating = true;
        return spin.from + (spin.to - spin.from) * value;
    }

    private dirAngle(dir: Direction): number {
        switch (dir) {
            case 'E': return 0;
            case 'S': return Math.PI / 2;
            case 'W': return Math.PI;
            case 'N': return -Math.PI / 2;
        }
    }

    /** Pieces that are already gone from game state and exist only to leave. */
    private drawDepartingPieces(): void {
        for (const [key, grown] of this.linkFrame) {
            if (this.links.state.has(key)) continue;
            const info = this.linkMemo.get(key);
            if (!info) continue;
            this.strokeLink(info.from, info.to, info.color, grown);
        }
        for (const [key, grown] of this.landingFrame) {
            if (this.landings.state.has(key)) continue;
            const info = this.landingMemo.get(key);
            if (!info) continue;
            this.drawLandingAt(parsePositionKey(key), info.color, 1, grown);
        }
        for (const [key, grown] of this.launcherFrame) {
            if (this.launchers.state.has(key)) continue;
            const info = this.launcherMemo.get(key);
            if (!info) continue;
            this.drawLauncher(parsePositionKey(key), info.color, this.launcherAngleAt(key), grown);
        }
    }

    /** The two gray lines splitting the grid into quarters, in the Ignition wall style. */
    private drawQuarterBoundaries(game: JumpsGame): void {
        if (!game.hasQuarters) return;
        const { padding, cellSize } = this.config;
        const span = this.grid.size * cellSize;
        const mid = padding + game.half * cellSize;

        this.ctx.save();
        this.ctx.strokeStyle = this.jumpsConfig.boundaryColor;
        this.ctx.lineWidth = this.jumpsConfig.boundaryWidth;
        this.ctx.lineCap = 'butt';

        this.ctx.beginPath();
        this.ctx.moveTo(mid, padding);
        this.ctx.lineTo(mid, padding + span);
        this.ctx.moveTo(padding, mid);
        this.ctx.lineTo(padding + span, mid);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /**
     * A faint dashed ring on every digit that has no landing yet — every digit must
     * eventually host one, so this marks where a ramp still has to start. Purely a hint:
     * it vanishes the moment a real landing takes the cell.
     */
    private drawDigitGhosts(game: JumpsGame): void {
        const ghost = colorWithAlpha('gray', 0.55);
        for (const [, pos] of game.getDigitPositions()) {
            if (game.getRampLandingAt(pos)) continue;
            const c = this.getCellCenter(pos);
            // Left unfilled: an arc already aimed here, and its arrival dot, stay visible.
            this.drawLandingCircle(c.x, c.y, ghost, undefined, false);
        }
    }

    private drawRamps(game: JumpsGame, colors: ReturnType<JumpsGame['computeRampColors']>): void {
        for (const [, ramp] of game.getRamps()) {
            // One run per quarter the ramp walks in: a ramp through a gate stops at the
            // gate and picks up again at its pair, rather than crossing the board.
            const bodyColor = this.color(game.getRampColor(ramp, colors));
            for (const run of game.getBodyRuns(ramp)) this.strokeRun(run, bodyColor);

            const landing = game.getLanding(ramp);
            this.drawLandingAt(landing, this.color(game.getLandingColor(ramp, colors)), 1,
                this.landingFrame.get(positionKey(landing)) ?? 1);

            const launcher = game.getLauncher(ramp);
            const key = positionKey(launcher);
            this.drawLauncher(launcher, this.color(game.getLauncherColor(ramp, colors)),
                this.launcherAngleAt(key), this.launcherFrame.get(key) ?? 1);
        }
    }

    /**
     * One run of a body, link by link. The line goes centre to centre, straight under
     * the landing circle and the launcher triangle: both are drawn over it afterwards
     * and both are opaque, so they mask the overshoot — which is what makes the joins
     * flush with no trimming arithmetic, at any jump direction.
     *
     * Drawing each link separately rather than as one polyline is what lets a run be
     * built up a cell at a time; at rest it draws the same marks, since a round cap at
     * a shared cell centre is the same mark as a round join.
     */
    private strokeRun(run: Position[], color: string): void {
        for (let i = 1; i < run.length; i++) {
            const grown = this.linkFrame.get(this.linkKey(run[i - 1], run[i])) ?? 1;
            this.strokeLink(run[i - 1], run[i], color, grown);
        }
    }

    /** The `grown` fraction of one body link, drawn on from its landing-side end. */
    private strokeLink(from: Position, to: Position, color: string, grown: number): void {
        if (grown <= 0) return;
        const a = this.getCellCenter(from);
        const b = this.getCellCenter(to);

        this.ctx.save();
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.jumpsConfig.rampWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(a.x, a.y);
        this.ctx.lineTo(a.x + (b.x - a.x) * grown, a.y + (b.y - a.y) * grown);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /** A landing ring on a cell, at `scale` of the full radius and `grown` of that. */
    private drawLandingAt(cell: Position, color: string, scale: number, grown: number): void {
        if (grown <= 0) return;
        const c = this.getCellCenter(cell);
        this.drawLandingCircle(c.x, c.y, color, this.jumpsConfig.landingRadius * scale * grown);
    }

    /**
     * The jumper's landing pad. Filled with the background colour, so it masks both the
     * arcs arriving at it and the ramp body running under it. A digit on the cell is
     * drawn later still, so it stays legible on top.
     */
    private drawLandingCircle(
        x: number,
        y: number,
        color: string,
        radius?: number,
        filled: boolean = true
    ): void {
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius ?? this.jumpsConfig.landingRadius, 0, Math.PI * 2);
        if (filled) {
            this.ctx.fillStyle = this.config.backgroundColor;
            this.ctx.fill();
        }
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 3;
        this.ctx.stroke();
        this.ctx.restore();
    }

    /**
     * Triangle pointing the way the jumper leaves, like an Antennas emitter. Its base
     * sits on the cell centre, so the ramp body meets it flush.
     *
     * It grows tip-first out of that base — the way it throws the jumper — so `grown`
     * scales the height and leaves the base alone. At `grown: 0` the triangle is
     * degenerate and covers nothing, which is exactly right. Turning it is a rotation
     * about the cell centre, which is why the geometry is built at an angle rather
     * than per direction.
     */
    private drawLauncher(cell: Position, color: string, angle: number, grown: number): void {
        if (grown <= 0) return;
        const halfBase = this.jumpsConfig.launcherSize;
        const height = halfBase * 1.4 * grown;
        // Base pulled back behind the cell centre, so the body — which runs all the way to
        // the centre — ends underneath the triangle rather than short of it.
        const baseOffset = halfBase * 0.25;
        const c = this.getCellCenter(cell);

        this.ctx.save();
        this.ctx.translate(c.x, c.y);
        this.ctx.rotate(angle);
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(height - baseOffset, 0);
        this.ctx.lineTo(-baseOffset, -halfBase);
        this.ctx.lineTo(-baseOffset, halfBase);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
    }

    /**
     * The jump itself: an arc from launcher to landing, bowed to the right of travel so
     * a pair of ramps jumping both ways between two cells reads as two arcs, not one.
     */
    private drawJumpArcs(
        game: JumpsGame, colors: ReturnType<JumpsGame['computeRampColors']>
    ): void {
        for (const [, ramp] of game.getRamps()) {
            const target = game.getRampTarget(ramp);
            if (!target) continue;

            const from = this.getCellCenter(game.getLauncher(ramp));
            const to = this.getCellCenter(target);
            const color = this.color(game.getLauncherColor(ramp, colors));

            // Control point: midpoint pushed to the right of travel (screen y grows down,
            // so rotating the heading clockwise is (x, y) -> (-y, x)).
            const dx = to.x - from.x, dy = to.y - from.y;
            const len = Math.hypot(dx, dy) || 1;
            const bulge = this.config.cellSize * 0.55;
            const cxp = (from.x + to.x) / 2 + (-dy / len) * bulge * 2;
            const cyp = (from.y + to.y) / 2 + (dx / len) * bulge * 2;

            // Sampled so both ends can be trimmed clear of the launcher and landing marks.
            const at = (t: number) => {
                const u = 1 - t;
                return {
                    x: u * u * from.x + 2 * u * t * cxp + t * t * to.x,
                    y: u * u * from.y + 2 * u * t * cyp + t * t * to.y
                };
            };

            this.ctx.save();
            this.ctx.strokeStyle = color;
            this.ctx.lineWidth = this.jumpsConfig.arcWidth;
            this.ctx.setLineDash([6, 4]);
            this.ctx.beginPath();
            const STEPS = 24;
            for (let i = 0; i <= STEPS; i++) {
                const p = at(i / STEPS);
                if (i === 0) this.ctx.moveTo(p.x, p.y);
                else this.ctx.lineTo(p.x, p.y);
            }
            this.ctx.stroke();

            // Nothing catches the jumper here yet — mark where it comes down.
            if (!game.getTargetRamp(ramp)) {
                this.ctx.setLineDash([]);
                this.ctx.fillStyle = color;
                this.ctx.beginPath();
                this.ctx.arc(to.x, to.y, 4, 0, Math.PI * 2);
                this.ctx.fill();
            }
            this.ctx.restore();
        }
    }

    /** Body strokes of the ramp being built — drawn under the gates (see render). */
    private drawCurrentPathBody(game: JumpsGame): void {
        if (game.getCurrentPath().length === 0) return;
        for (const run of game.getBuildingRuns()) {
            this.strokeRun(run, this.jumpsConfig.buildingColor);
        }
    }

    /** Landing and building-end circles — drawn over the gates, so they stay centered. */
    private drawCurrentPathMarkers(game: JumpsGame): void {
        const path = game.getCurrentPath();
        if (path.length === 0) return;

        const color = this.jumpsConfig.buildingColor;
        this.drawLandingAt(path[0], color, 1, this.landingFrame.get(positionKey(path[0])) ?? 1);
        if (path.length < 2) return;

        // Smaller circle on the building end — the launcher is not placed yet. It is the
        // pen: it rides the head of the link being drawn on, so the ramp visibly reaches
        // the cell the player just claimed.
        const runs = game.getBuildingRuns();
        const tip = this.buildTip(runs[runs.length - 1]);
        this.drawLandingCircle(tip.x, tip.y, color, this.jumpsConfig.landingRadius * 0.6);
    }

    /** Where the build currently ends on screen — the head of its last link while that
     *  link is still being drawn on, its end cell once it has landed. */
    private buildTip(run: Position[]): { x: number; y: number } {
        const end = this.getCellCenter(run[run.length - 1]);
        if (run.length < 2) return end;

        const grown = this.linkFrame.get(this.linkKey(run[run.length - 2], run[run.length - 1]));
        if (grown === undefined) return end;

        const prev = this.getCellCenter(run[run.length - 2]);
        return { x: prev.x + (end.x - prev.x) * grown, y: prev.y + (end.y - prev.y) * grown };
    }

    private drawWarnings(game: JumpsGame): void {
        for (const cell of game.getCrashCells()) this.drawCellBorder(cell, COLORS.red);
        for (const cell of game.getDoubleTargetedCells()) this.drawCellBorder(cell, COLORS.red);
    }
}
